import type { ChangeEvent, FocusEvent, KeyboardEvent, Ref } from "react";

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  inputRef?: Ref<HTMLInputElement> | ((element: HTMLInputElement | null) => void);
  onEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;
  step?: number;
  minQuantity?: number;
}

const QuantityInput = ({
  value,
  onChange,
  inputRef,
  onEnter,
  step = 1,
  minQuantity = 0,
}: QuantityInputProps) => {
  const effectiveStep =
    Number.isFinite(Number(step)) && Number(step) > 0 ? Number(step) : 1;

  const normalize = (raw: string | number) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 0;
    }
    const minAllowed = Math.max(minQuantity, effectiveStep);
    const rounded = Math.ceil(parsed / effectiveStep) * effectiveStep;
    return Math.max(minAllowed, rounded);
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = normalize(event.target.value);
    onChange(nextValue);
  };

  const adjust = (delta: number) => {
    const current = Number.isFinite(value) ? value : 0;
    const threshold = Math.max(minQuantity || 0, effectiveStep);
    const candidate = current + delta;

    if (delta < 0 && candidate <= 0) {
      onChange(0);
      return;
    }
    const normalized = normalize(candidate);
    if (delta < 0 && normalized <= threshold && normalized !== 0) {
      onChange(0);
      return;
    }
    onChange(normalized);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && onEnter) {
      event.preventDefault();
      onEnter(event);
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    const select = () => event.currentTarget.select();
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(select);
    } else {
      setTimeout(select, 0);
    }
  };

  return (
    <div className="quantity-input">
      <button
        type="button"
        onClick={() => adjust(-effectiveStep)}
        disabled={value <= 0}
      >
        -
      </button>
      <input
        type="number"
        min="0"
        step={effectiveStep}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        ref={inputRef}
      />
      <button type="button" onClick={() => adjust(effectiveStep)}>
        +
      </button>
    </div>
  );
};

export default QuantityInput;
